import clsx from 'clsx';
import { Star } from 'lucide-react';

import { useUiPreferences } from '../preferences/ui-preferences';

import type { AutomationSummary } from './automation-summary';

/** Favoris de l'utilisateur connecté (enregistrés avec ses préférences, côté serveur). */
export function useFavorites() {
  const { data, update } = useUiPreferences();
  const ids = data?.favoriteAutomationIds ?? [];
  const favorites = new Set(ids);
  function toggle(id: string) {
    const next = favorites.has(id) ? ids.filter((x) => x !== id) : [...ids, id];
    update.mutate({ favoriteAutomationIds: next });
  }
  return { favorites, toggle, pending: update.isPending };
}

/**
 * Texte comparable : minuscules, sans accents (décomposition NFD puis retrait des
 * accents combinants U+0300 à U+036F).
 */
function normalize(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Recherche sur le nom, la description, la catégorie, l'identifiant et l'organisation. */
export function matchesSearch(a: AutomationSummary, query: string): boolean {
  const q = normalize(query.trim());
  if (!q) return true;
  const haystack = normalize(
    [a.name, a.description, a.category, a.slug, a.organization?.name].filter(Boolean).join(' '),
  );
  return q.split(/\s+/).every((word) => haystack.includes(word));
}

/** Favoris en tête, ordre d'origine conservé à l'intérieur de chaque groupe. */
export function favoritesFirst<T extends { id: string }>(rows: T[], favorites: Set<string>): T[] {
  return [...rows.filter((r) => favorites.has(r.id)), ...rows.filter((r) => !favorites.has(r.id))];
}

/** Étoile pour épingler / désépingler une automatisation. */
export function FavoriteStar({
  automation,
  favorite,
  onToggle,
  disabled,
}: {
  automation: { id: string; name: string };
  favorite: boolean;
  onToggle: (id: string) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(automation.id)}
      disabled={disabled}
      aria-pressed={favorite}
      aria-label={
        favorite
          ? `Retirer « ${automation.name} » des favoris`
          : `Ajouter « ${automation.name} » aux favoris`
      }
      title={favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
      className={clsx(
        'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:opacity-60',
        favorite ? 'text-amber-500' : 'text-slate-300 hover:text-slate-500',
      )}
    >
      <Star className={clsx('h-4 w-4', favorite && 'fill-current')} aria-hidden="true" />
    </button>
  );
}
